import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipSize = 'sm' | 'md' | 'lg';
export type TooltipAlign = 'center' | 'start' | 'end';

interface TooltipProps {
  children: ReactNode;
  text: string;
  position?: TooltipPosition;
  align?: TooltipAlign;
  size?: TooltipSize;
  className?: string;
  disabled?: boolean;
  noWrap?: boolean;
}

const GAP = 8; // distancia entre trigger y tooltip

const Tooltip: React.FC<TooltipProps> = ({
  children,
  text,
  position = 'top',
  align = 'center',
  size = 'md',
  className = '',
  disabled = false,
  noWrap = false
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const sizeClasses = {
    sm: 'max-w-xs text-ui-sm',
    md: 'max-w-sm text-ui-sm',
    lg: 'max-w-xl text-xs'
  };

  const tooltipStyle = size === 'lg' ? { whiteSpace: 'pre-line' as const } : undefined;

  const handleMouseEnter = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    let top = 0;
    let left = 0;

    const hLeft   = rect.left + scrollX;
    const hCenter = rect.left + scrollX + rect.width / 2;
    const hRight  = rect.right + scrollX;

    switch (position) {
      case 'top':
        top  = rect.top + scrollY - GAP;
        left = align === 'start' ? hLeft : align === 'end' ? hRight : hCenter;
        break;
      case 'bottom':
        top  = rect.bottom + scrollY + GAP;
        left = align === 'start' ? hLeft : align === 'end' ? hRight : hCenter;
        break;
      case 'left':
        top  = rect.top + scrollY + rect.height / 2;
        left = hLeft - GAP;
        break;
      case 'right':
        top  = rect.top + scrollY + rect.height / 2;
        left = hRight + GAP;
        break;
    }

    setCoords({ top, left });
  }, [position]);

  const handleMouseLeave = useCallback(() => {
    setCoords(null);
  }, []);

  useEffect(() => {
    if (disabled) setCoords(null);
  }, [disabled]);

  // Clases de transformación según posición + align
  const transformClasses: Record<TooltipPosition, string> = {
    top:    align === 'start' ? '-translate-y-full' : align === 'end' ? '-translate-x-full -translate-y-full' : '-translate-x-1/2 -translate-y-full',
    bottom: align === 'start' ? '' : align === 'end' ? '-translate-x-full' : '-translate-x-1/2',
    left:   '-translate-x-full -translate-y-1/2',
    right:  '-translate-y-1/2',
  };

  // Posición de la flecha según posición + align
  const arrowH = align === 'start' ? '12px' : align === 'end' ? 'calc(100% - 12px)' : '50%';
  const arrowHTransform = align === 'center' ? 'translateX(-50%)' : align === 'end' ? 'translateX(-50%)' : 'translateX(-50%)';

  const arrowPositionStyle: Record<TooltipPosition, React.CSSProperties> = {
    top:    { top: '100%', left: arrowH, transform: arrowHTransform, borderTopColor: 'var(--text-primary)', borderRightColor: 'transparent', borderBottomColor: 'transparent', borderLeftColor: 'transparent' },
    bottom: { bottom: '100%', left: arrowH, transform: arrowHTransform, borderBottomColor: 'var(--text-primary)', borderRightColor: 'transparent', borderTopColor: 'transparent', borderLeftColor: 'transparent' },
    left:   { left: '100%', top: '50%', transform: 'translateY(-50%)', borderLeftColor: 'var(--text-primary)', borderRightColor: 'transparent', borderTopColor: 'transparent', borderBottomColor: 'transparent' },
    right:  { right: '100%', top: '50%', transform: 'translateY(-50%)', borderRightColor: 'var(--text-primary)', borderLeftColor: 'transparent', borderTopColor: 'transparent', borderBottomColor: 'transparent' },
  };

  return (
    <div
      ref={wrapperRef}
      className={`inline-flex items-center align-middle ${className}`}
      onMouseEnter={disabled ? undefined : handleMouseEnter}
      onMouseLeave={disabled ? undefined : handleMouseLeave}
    >
      {children}
      {!disabled && coords && createPortal(
        <span
          className={`
            fixed px-3 py-2 rounded-md
            transition-all duration-200 z-[9999] pointer-events-none
            bg-[var(--text-primary)] text-[var(--surface-0)] shadow-lg
            ${sizeClasses[size]}
            ${transformClasses[position]}
            ${noWrap ? 'whitespace-nowrap' : ''}
          `}
          style={{ top: coords.top, left: coords.left, ...tooltipStyle }}
        >
          {text}
          <span
            className="absolute w-0 h-0 border-[5px]"
            style={arrowPositionStyle[position]}
          />
        </span>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;
